import React, { useMemo, useState, useEffect, useCallback } from "react";
import { FiSearch, FiSave } from "react-icons/fi";

// AG GRID STYLES
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import Input from "@components/Input";
import RoleGroupService from "@service/RoleGroupService";
import RoleService from "@service/RoleService";
// import { use } from "i18next";
import { IPermissionDTO } from "@interfaces/IPermissionDTO";
import { IRoleGroupDTO } from "@interfaces/IRoleGroupDTO";
import { SelectFilter } from "@components/SelectFilter";
import HeadlessDialog from "@components/Dialog";
import { ISelect } from "@interfaces/ISelect";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";

interface RoleItem {
  id: number;
  name: string;
}

interface GroupedPermission {
  role_id: number;
  role_name: string;
  routine_id: number;
  routine_name: string;
  functionalities: Array<{
    functionality_id: number;
    functionality_name: string;
    has_permission: boolean;
  }>;
}

export const PolicyView: React.FC = () => {
  // Roles
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRoleGroup, setSelectedRoleGroup] = useState<ISelect | null>(
    null
  );
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [permissionRows, setPermissionRows] = useState<IPermissionDTO[]>([]);
  const [permissionOriginalRows, setPermissionOriginalRows] = useState<
    IPermissionDTO[]
  >([]);
  const [groupedPermissions, setGroupedPermissions] = useState<
    GroupedPermission[]
  >([]);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [searchVal, setsearchVal] = useState("");
  const [roleGroups, setRoleGroups] = useState<ISelect[]>([]);
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogDescription, setDialogDescription] =
    useState<React.ReactNode>("");
  // Added: persistent inline status banner after save
  const [saveMessage, setSaveMessage] = useState<{
    text: string;
    type: "success" | "error" | "info" | null;
  }>({ text: "", type: null });

  // Derived: true when any permission differs from the original snapshot
  const hasChanges = useMemo(() => {
    if (permissionRows.length !== permissionOriginalRows.length) return false;
    return permissionRows.some(
      (row, i) =>
        row.has_permission !== permissionOriginalRows[i].has_permission
    );
  }, [permissionRows, permissionOriginalRows]);

  // Context menu hook
  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});

  // Prevent default browser context menu
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  function onGridReady(params: GridReadyEvent): void {
    setGridApi(params.api);
  }

  const updateSearchQuery = (val: string): void => {
    if (gridApi !== undefined) {
      setsearchVal(val);
      gridApi!.setQuickFilter(val);
    }
  };

  useEffect(() => {
    async function loadRoleGroups() {
      try {
        const fetchedRoleGroups = await RoleGroupService.getAllRoleGroups();
        setRoleGroups(
          (fetchedRoleGroups ?? []).map((roleGroup: IRoleGroupDTO) => ({
            id: roleGroup.id,
            value: roleGroup.name,
          }))
        );
        if (fetchedRoleGroups.length > 0) {
          const selectedRoleGroup = fetchedRoleGroups[0];
          setSelectedRoleGroup({
            id: selectedRoleGroup.id,
            value: selectedRoleGroup.name,
          });
        }
      } catch (e) {}
    }
    loadRoleGroups();
  }, []);

  useEffect(() => {
    async function loadPermissions() {
      try {
        if (selectedRoleGroup === null) return;
        const permissions = await RoleService.fetchAllRolesForPermissions(
          selectedRoleGroup.id
        );
        setPermissionRows(permissions);
        const permissionsCopy = permissions.map((p) => ({
          ...p,
        }));
        setPermissionOriginalRows(permissionsCopy);
        const roles = await RoleService.fetchRolesByRoleGroup(
          selectedRoleGroup.id
        );
        const mapped: RoleItem[] = roles.map((r) => ({
          id: r.id,
          name: r.name,
        }));
        setRoles(mapped);
        if (mapped.length) setSelectedRoleId(mapped[0].id);
      } catch (e) {}
    }
    loadPermissions();
  }, [selectedRoleGroup]);

  // Group permissions by routine for the selected role
  useEffect(() => {
    if (!selectedRoleId) return;

    const filtered = permissionRows.filter((p) => p.role_id === selectedRoleId);
    const grouped = filtered.reduce<Record<string, GroupedPermission>>(
      (acc, perm) => {
        const key = `${perm.routine_name}`;
        if (!acc[key]) {
          acc[key] = {
            role_id: perm.role_id,
            role_name: perm.role_name,
            routine_id: perm.routine_id,
            routine_name: perm.routine_name,
            functionalities: [],
          };
        }
        acc[key].functionalities.push({
          functionality_id: perm.functionality_id,
          functionality_name: perm.functionality_name,
          has_permission: perm.has_permission,
        });
        return acc;
      },
      {}
    );

    setGroupedPermissions(Object.values(grouped));
  }, [permissionRows, selectedRoleId]);

  const setPermissionsAG = (routineId: number, functionalityId: number) => {
    // Clear success banner when new changes are made so user knows they must Save again
    if (saveMessage.type === "success") {
      setSaveMessage({ text: "", type: null });
    }
    const updatedRows = permissionRows.map((row) => {
      if (
        row.role_id === selectedRoleId &&
        row.routine_id === routineId &&
        row.functionality_id === functionalityId
      ) {
        return { ...row, has_permission: !row.has_permission };
      }
      return row;
    });
    setPermissionRows(updatedRows);
  };

  const columnDefs = [
    {
      headerName: "Routine",
      field: "routine_name",
      width: 300,
      filter: true,
      // autoHeight: true,
      // wrapText: true,
    },
    {
      headerName: "Functionalities",
      field: "functionalities",
      flex: 1,
      // autoHeight: true,
      cellRenderer: (params: any): JSX.Element => {
        const functionalities = params.data.functionalities || [];
        return (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-py-2">
            {functionalities.map((func: any) => {
              const checked = !!func.has_permission;
              const toggle = () => {
                setPermissionsAG(params.data.routine_id, func.functionality_id);
              };
              return (
                <div
                  key={func.functionality_id}
                  className="tw-flex tw-items-center tw-gap-2 tw-min-w-[120px]"
                >
                  <input type="checkbox" checked={checked} onChange={toggle} />
                  <span className="tw-text-sm">{func.functionality_name}</span>
                </div>
              );
            })}
          </div>
        );
      },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: false,
      filter: true,
      suppressMenu: true,
    }),
    []
  );

  const handleSave = async () => {
    setsearchVal("");
    gridApi!.setQuickFilter("");
    // Find changed permissions
    const permissionUpdated = permissionRows.filter((newPermission, index) => {
      const original = permissionOriginalRows[index];
      return original.has_permission !== newPermission.has_permission;
    });
    if (!permissionUpdated || permissionUpdated.length <= 0) {
      setDialogTitle("No Changes");
      setDialogDescription("There are no permission changes to submit.");
      setShowDialog(true);
      setSaveMessage({ text: "No permission changes to save.", type: "info" });
      return;
    }
    try {
      await RoleService.editPermissions(permissionUpdated);
      setDialogTitle("Permissions Updated");
      setDialogDescription(
        <div className="tw-space-y-2">
          <p>Permissions updated successfully.</p>
        </div>
      );
      setShowDialog(true);
      // refresh originals so subsequent saves only show new diffs
      const copy = permissionRows.map((p) => ({ ...p }));
      setPermissionOriginalRows(copy);
      setSaveMessage({
        text: "Permissions updated successfully.",
        type: "success",
      });
    } catch (err) {
      setDialogTitle("Update Failed");
      setDialogDescription(
        <div className="tw-space-y-2">
          <p>Failed to update permissions.</p>
          <pre className="tw-bg-red-50 tw-text-red-600 tw-p-2 tw-rounded tw-overflow-auto tw-text-xs">
            {String(err)}
          </pre>
        </div>
      );
      setShowDialog(true);
      setSaveMessage({ text: "Failed to update permissions.", type: "error" });
    }
  };

  function handleChangeRoleGroup(value: ISelect) {
    setSelectedRoleGroup(value);
  }

  function handleChangeRole(id: number) {
    setSelectedRoleId(id);
  }

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-w-full tw-gap-4 tw-p-4 tw-mt-0">
      {showDialog && (
        <HeadlessDialog
          title={dialogTitle}
          description={dialogDescription}
          isOpen={showDialog}
          setIsOpen={setShowDialog}
        >
          <></>
        </HeadlessDialog>
      )}
      {/* Top bar for role group selection */}
      <div className="tw-flex tw-items-center tw-justify-start tw-gap-4">
        <SelectFilter
          className="tw-ml-1 tw-w-[253px]"
          select={roleGroups.length > 0 ? selectedRoleGroup : null}
          options={roleGroups}
          onSelect={handleChangeRoleGroup}
          placeholder="Select a role group..."
          disabled={false}
        />
      </div>

      <div className="tw-flex tw-h-full tw-w-full tw-gap-6">
        <div className="tw-w-64 tw-flex tw-flex-col tw-border tw-border-gray-200 tw-rounded tw-bg-white tw-p-3 tw-h-full">
          <h3 className="tw-font-semibold tw-text-sm tw-mb-2">Roles</h3>
          <ul className="tw-overflow-auto tw-space-y-1 tw-flex-1">
            {roles.map((r) => (
              <li
                key={r.id}
                onClick={() => handleChangeRole(r.id)}
                className={
                  "tw-cursor-pointer tw-rounded tw-px-2 tw-py-1 tw-text-sm " +
                  (r.id === selectedRoleId
                    ? "tw-bg-blue-600 tw-text-white"
                    : "hover:tw-bg-gray-100")
                }
              >
                {r.name}
              </li>
            ))}
            {roles.length === 0 && (
              <li className="tw-text-xs tw-text-gray-400">No roles</li>
            )}
          </ul>
        </div>

        {/* Permissions grid */}
        <div className="tw-flex-1 tw-flex tw-flex-col">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-2">
            <h3 className="tw-font-semibold tw-text-sm">Permissions</h3>

            {/* NEW: permissions quick search */}
            <div className="tw-flex  tw-h-[3.5vh] tw-w-[40rem] tw-mr-auto">
              <Input
                icon={FiSearch}
                containerStyle={{ marginRight: 4 }}
                id="search"
                type="text"
                name="search"
                placeholder="search"
                onChange={(e: any) => updateSearchQuery(e.target.value)}
                disabled={false}
                value={searchVal}
              />
            </div>
            {saveMessage.type && (
              <div
                className={
                  "tw-text-xs tw-rounded tw-px-3 tw-flex tw-items-start tw-gap-2 " +
                  (saveMessage.type === "success"
                    ? "tw-bg-green-50 tw-text-green-700 tw-border tw-border-green-200"
                    : saveMessage.type === "error"
                    ? "tw-bg-red-50 tw-text-red-700 tw-border tw-border-red-200"
                    : "tw-bg-blue-50 tw-text-blue-700 tw-border tw-border-blue-200")
                }
              >
                <span className="tw-font-medium">
                  {saveMessage.type === "success"
                    ? "Success"
                    : saveMessage.type === "error"
                    ? "Error"
                    : "Info"}
                  :
                </span>
                <span>{saveMessage.text}</span>
                <button
                  onClick={() => setSaveMessage({ text: "", type: null })}
                  className="tw-ml-auto tw-text-[10px] hover:tw-underline"
                >
                  Dismiss
                </button>
              </div>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className={
                "tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-px-3 tw-py-1.5 tw-rounded tw-transition-colors " +
                (hasChanges
                  ? "tw-bg-blue-600 tw-text-white hover:tw-bg-blue-500"
                  : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed")
              }
            >
              <FiSave className="tw-w-3.5 tw-h-3.5" />
              Save
            </button>
          </div>
          <div
            className="ag-theme-balham tw-h-[650px] tw-w-full"
            onContextMenu={handleContextMenu}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={groupedPermissions}
              gridOptions={{}}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              onCellContextMenu={handleCellContextMenu}
              rowSelection="multiple"
              colResizeDefault="shift"
              animateRows
              pivotPanelShow="always"
            />
            {ContextMenuComponent}
          </div>
        </div>
      </div>
    </div>
  );
};
